import { useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import {
  Brain,
  Workflow,
  Code2,
  Shield,
  Terminal,
  MessageSquare,
  ListChecks,
  Github,
  Linkedin,
  Check,
} from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '~/components/ui/accordion';
import { useAuth } from '~/hooks/useAuth';
import { modules, totalLessons, totalMinutes, moduleDuration } from '~/data/courseData';
import type { Route } from './+types/_index';

export const links: Route.LinksFunction = () => [];


/* ------------------------------------------------------------------ */
/*  DATA                                                               */
/* ------------------------------------------------------------------ */

const learnItems = [
  {
    icon: ListChecks,
    title: '5-Phase Framework',
    desc: 'Requirements → Architecture → Tasks → TDD → Review. A repeatable pipeline for every project.',
  },
  {
    icon: Terminal,
    title: 'Claude Code & Skills',
    desc: 'The CLI, slash commands, .claude/ directory, and custom skills that automate your workflow.',
  },
  {
    icon: Workflow,
    title: 'Agent Architecture',
    desc: 'Design agents that decompose work into sequential skills and parallel subagents.',
  },
  {
    icon: Brain,
    title: 'LLM Fundamentals',
    desc: 'How transformers work, token prediction, context windows, and the limits of LLMs.',
  },
  {
    icon: MessageSquare,
    title: 'Precision Prompting',
    desc: 'Write explicit prompts, use thinking modes, and design skills Claude follows every time.',
  },
  {
    icon: Shield,
    title: 'Cost Controls & Guardrails',
    desc: 'External scripts, hooks, built-in read-only agents, and token-saving patterns.',
  },
];

const totalH = Math.floor(totalMinutes / 60);
const totalM = Math.round(totalMinutes % 60);
const LINKEDIN = 'https://www.linkedin.com/in/fareal';
const GITHUB = 'https://github.com/farealahmed';

/* ------------------------------------------------------------------ */
/*  HOME COMPONENT                                                     */
/* ------------------------------------------------------------------ */

export default function Home() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isLoggedIn } = useAuth();
  const [searchParams] = useSearchParams();

  /* No-access toast */
  useEffect(() => {
    if (searchParams.get('reason') === 'no_access') {
      toast.error('Log in to access lessons', {
        description: 'Sign in to unlock all video lessons and track your progress.',
        duration: 6000,
      });
    }
  }, [searchParams]);

  /* GSAP animations — hero runs immediately, scroll sections deferred */
  useEffect(() => {
    let ctx: { revert: () => void } | undefined;
    let cancelled = false;
    let idleHandle: number | undefined;

    const initGSAP = async () => {
      const gsap = (await import('gsap')).default;
      if (cancelled) return;
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);
      if (cancelled) return;

      ctx = gsap.context(() => {
        // Hero load animations — run immediately for LCP
        const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
        heroTl
          .fromTo('.hero-eyebrow', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 0)
          .fromTo('.hero-title', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.5 }, 0.15)
          .fromTo('.hero-subtitle', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4 }, 0.3)
          .fromTo('.hero-author', { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.4 }, 0.4)
          .fromTo('.hero-cta', { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.4 }, 0.45)
          .fromTo('.hero-code', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 }, 0.6);

        // Scroll-triggered sections — defer to idle time to avoid forced reflow during paint
        const initScrollAnimations = () => {
          if (cancelled) return;
          const sections = [
            '.section-learn',
            '.section-curriculum',
            '.section-instructor',
            '.section-pricing',
          ];

          sections.forEach((sel) => {
            const el = containerRef.current?.querySelector(sel);
            if (!el) return;
            gsap.fromTo(
              el.querySelectorAll('.reveal'),
              { opacity: 0, y: 40 },
              {
                opacity: 1,
                y: 0,
                duration: 0.6,
                stagger: 0.1,
                ease: 'power3.out',
                scrollTrigger: {
                  trigger: el,
                  start: 'top 80%',
                  toggleActions: 'play none none none',
                },
              }
            );
          });

          // Curriculum accordion stagger
          gsap.fromTo(
            '.curriculum-module',
            { opacity: 0, x: 20 },
            {
              opacity: 1,
              x: 0,
              duration: 0.5,
              stagger: 0.08,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: '.section-curriculum',
                start: 'top 80%',
                toggleActions: 'play none none none',
              },
            }
          );

        };

        const hasRequestIdleCallback = typeof window !== 'undefined' && 'requestIdleCallback' in window;
        if (hasRequestIdleCallback) {
          idleHandle = requestIdleCallback(() => initScrollAnimations());
        } else {
          idleHandle = window.setTimeout(initScrollAnimations, 100);
        }
      }, containerRef.current ?? undefined);
    };

    initGSAP().catch(console.error);
    return () => {
      cancelled = true;
      if (idleHandle !== undefined) {
        const hasCancelIdleCallback = typeof window !== 'undefined' && 'cancelIdleCallback' in window;
        if (hasCancelIdleCallback) {
          cancelIdleCallback(idleHandle);
        } else {
          clearTimeout(idleHandle);
        }
      }
      ctx?.revert();
    };
  }, []);

  return (
    <div ref={containerRef}>
      {/* ============================================================ */}
      {/* SECTION 1 — HERO                                             */}
      {/* ============================================================ */}
      <section className="relative min-h-[90dvh] flex items-center bg-bg-primary overflow-hidden">
        {/* Gradient halo */}
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(34,211,238,0.06) 0%, transparent 70%)',
          }}
        />

        <div className="max-w-content mx-auto px-6 lg:px-10 py-20 pt-28 w-full">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Text content */}
            <div className="flex-1 text-center lg:text-left">
{/*               <p className="hero-eyebrow text-section-label mb-4">
                Agentic Software Engineering — The Complete Course
              </p> */}

{/*               <h1 className="hero-title font-heading text-hero font-medium text-text-primary tracking-tight mb-6">
                Speed without
                <br />
                discipline
                <br />
                <span className="text-text-primary">is not </span>
                <span className="text-accent-cyan">engineering.</span>
              </h1>

              <p className="hero-subtitle font-body text-body text-text-secondary max-w-[560px] mx-auto lg:mx-0 mb-6">
                A three-act course on building real software with AI agents — the 5-phase framework, the skills that drive it, and the operating environment that keeps it safe.
              </p> */}

              <h1 className="hero-title font-heading text-hero font-medium text-text-primary tracking-tight mb-6">
                <span className="text-accent-cyan">Skills that compile.</span>
              </h1>

              <p className="hero-subtitle font-body text-body text-text-secondary max-w-[560px] mx-auto lg:mx-0 mb-6">
                Hands-on courses across software engineering — from fundamentals to frontier topics.
              </p>

              <div className="hero-author flex items-center justify-center lg:justify-start gap-3 mb-8">
                <img
                  src="/author-photo-64.webp"
                  alt="Fareal Ahmed"
                  loading="lazy"
                  decoding="async"
                  className="w-8 h-8 rounded-full object-cover border border-border-subtle"
                />
                <span className="font-body text-sm text-text-secondary">
                  by{' '}
                  <a
                    href={LINKEDIN}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-text-primary hover:text-accent-cyan hover:underline transition-colors"
                  >
                    Fareal Ahmed
                  </a>
                </span>
              </div>

              <div className="hero-cta">
                {isLoggedIn ? (
                  <Link
                    to="/dashboard"
                    className="inline-block font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-6 py-3 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline"
                  >
                    Go to Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="inline-block font-heading text-base font-medium bg-accent-cyan text-bg-primary px-8 py-4 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline"
                  >
                    Get access
                  </Link>
                )}
                <p className="font-body text-xs text-text-muted mt-3">
                  Free access. Sign in with your email.
                </p>
              </div>
            </div>

            {/* Code snippet card */}
            <div className="hero-code flex-1 w-full max-w-xl">
              <div className="bg-bg-elevated border border-border-subtle rounded-lg p-4 shadow-card-hover">
                {/* Terminal chrome */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                  <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                  <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                  <span className="ml-2 font-heading text-xs text-text-muted">
                    agent.ts
                  </span>
                </div>
                <pre className="font-mono text-[11px] leading-5 text-[#abb2bf] m-0 overflow-x-auto select-none">
{[
  [['kw','import'], [' { '], ['ty','LLMClient'], [', '], ['ty','Tool'], [', '], ['ty','Memory'], [' '], ['kw','from'], [' '], ['st','"./core"'], [';']],
  [],
  [['kw','const'], [' agent = '], ['kw','new'], [' '], ['ty','LLMClient'], ['({ model: '], ['st','"claude-opus-4"'], [' });']],
  [],
  [['kw','async'], [' '], ['kw','function'], [' '], ['fn','runAgent'], ['(task: '], ['ty','string'], [') {']],
  [['  '], ['kw','const'], [' memory = '], ['kw','new'], [' '], ['ty','Memory'], ['();']],
  [],
  [['  '], ['kw','while'], [' ('], ['kw','true'], [') {']],
  [['    '], ['kw','const'], [' { action } = '], ['kw','await'], [' agent.'], ['fn','think'], ['({']],
  [['      task,']],
  [['      context: memory.'], ['fn','get'], ['(),']],
  [['    });']],
  [],
  [['    '], ['kw','if'], [' (action.type === '], ['st','"tool_call"'], [') {']],
  [['      '], ['kw','const'], [' result = '], ['kw','await'], [' '], ['ty','Tool'], ['.'], ['fn','run'], ['(action);']],
  [['      memory.'], ['fn','append'], ['('], ['st','"tool"'], [', result);']],
  [['      '], ['kw','continue'], [';']],
  [['    }']],
  [],
  [['    '], ['kw','return'], [' action.content; '], ['cm','// final answer']],
  [['  }']],
  [['}']]
].map((line, i) => {
  const colors: Record<string, string> = {
    kw: '#c678dd', ty: '#e5c07b', fn: '#61afef', st: '#98c379', cm: '#5c6370',
  };
  return (
    <div key={i} className="flex">
      <span className="select-none w-6 text-right mr-4 text-[#3d4450] shrink-0">{i + 1}</span>
      <span>
        {line.length === 0 ? '\n' : line.map((tok, j) => {
          if (Array.isArray(tok) && tok.length === 2) {
            return <span key={j} style={{ color: colors[tok[0]] }}>{tok[1]}</span>;
          }
          return <span key={j}>{String(tok)}</span>;
        })}
      </span>
    </div>
  );
})}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 2 — WHAT YOU'LL LEARN                                */}
      {/* ============================================================ */}
      <section className="section-learn bg-bg-primary py-24 lg:py-32">
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <p className="reveal text-section-label mb-4">WHAT YOU'LL LEARN</p>
          <h2 className="reveal font-heading text-h2 text-text-primary mb-4">
            Theory → practice → pain → cure → mastery.
          </h2>
          <p className="reveal font-body text-body text-text-secondary max-w-[600px] mb-12">
            A five-part progression. The discipline never changes.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {learnItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="reveal group bg-bg-elevated border border-border-subtle rounded-lg p-6 hover:border-border-active hover:-translate-y-1 hover:shadow-card-hover transition-all duration-200"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-dim flex items-center justify-center mb-4">
                    <Icon size={20} className="text-accent-cyan" />
                  </div>
                  <h3 className="font-heading text-h4 text-text-primary mb-2">
                    {item.title}
                  </h3>
                  <p className="font-body text-sm text-text-secondary">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 3 — COURSE CURRICULUM                                */}
      {/* ============================================================ */}
      <section
        id="curriculum"
        className="section-curriculum bg-bg-primary py-24 lg:py-32"
      >
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left — sticky header */}
            <div className="lg:w-[40%]">
              <div className="lg:sticky lg:top-[120px]">
                <p className="reveal text-section-label mb-4">COURSE CURRICULUM</p>
                <h2 className="reveal font-heading text-h2 text-text-primary mb-4">
                  Everything inside.
                </h2>
                <p className="reveal font-body text-body text-text-secondary mb-4">
                  {modules.length} module{modules.length !== 1 ? 's' : ''}. {totalLessons} lesson{totalLessons !== 1 ? 's' : ''}. {totalH > 0 ? `${totalH}h ` : ''}{totalM}m of content.
                </p>

                <div className="reveal inline-flex items-center gap-2 bg-warning-dim border border-warning/30 rounded-full px-3 py-1 mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-warning animate-pulse shrink-0" />
                  <span className="font-body text-xs text-warning">Course content is actively being added</span>
                </div>

                <div className="reveal flex gap-8">
                  <div>
                    <p className="font-heading text-h3 text-text-primary">
                      {totalLessons}
                    </p>
                    <p className="font-body text-xs text-text-secondary mt-1">
                      Lessons
                    </p>
                  </div>
                  <div>
                    <p className="font-heading text-h3 text-text-primary">
                      {totalH > 0 ? `${totalH}h ` : ''}{totalM}m
                    </p>
                    <p className="font-body text-xs text-text-secondary mt-1">
                      Total
                    </p>
                  </div>
                  <div>
                    <p className="font-heading text-h3 text-text-primary">
                      {modules.length}
                    </p>
                    <p className="font-body text-xs text-text-secondary mt-1">
                      Modules
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — accordion */}
            <div className="lg:w-[60%]">
              <Accordion type="multiple" className="w-full">
                {modules.map((mod) => (
                  <AccordionItem
                    key={mod.number}
                    value={`module-${mod.number}`}
                    className="curriculum-module border-b border-border-subtle"
                  >
                    <AccordionTrigger className="py-4 hover:no-underline group">
                      <div className="flex items-center gap-4 text-left w-full pr-4">
                        <span className="font-heading text-xs text-accent-cyan shrink-0">
                          MODULE {mod.number}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-heading text-sm text-text-primary group-hover:text-accent-cyan transition-colors">
                              {mod.title}
                            </p>
                            {(mod.number === "01" || mod.number === "02" || mod.number === "03") && (
                              <span className="shrink-0 px-2 py-0.5 rounded-full bg-warning-dim text-warning font-body text-xs">
                                Free
                              </span>
                            )}
                          </div>
                          <p className="font-body text-xs text-text-muted mt-0.5">
                            {mod.lessons.length} lessons &middot;{' '}
                            {moduleDuration(mod.lessons)}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-2">
                      <div className="pl-0">
                        {mod.lessons.map((lesson) => (
                          <div
                            key={lesson.number}
                            className="py-3 px-4 border-t border-border-subtle"
                          >
                            <div className="flex items-center gap-4">
                              <span className="font-heading text-xs text-text-muted w-8 shrink-0">
                                {lesson.number}
                              </span>
                              <span className="flex-1 font-body text-sm text-text-primary">
                                {lesson.title}
                              </span>
                              {(mod.number === "01" || mod.number === "02" || mod.number === "03") && (
                                <span className="shrink-0 px-2 py-0.5 rounded-full bg-warning-dim text-warning font-body text-xs">
                                  Free
                                </span>
                              )}
                              <span className="font-body text-xs text-text-muted shrink-0">
                                {lesson.duration}
                              </span>
                            </div>
                            {lesson.description && lesson.description !== "Coming soon." && (
                              <p className="mt-1 pl-12 font-body text-xs text-text-muted leading-relaxed">
                                {lesson.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 4 — ABOUT THE INSTRUCTOR                             */}
      {/* ============================================================ */}
      <section className="section-instructor bg-bg-primary py-24 lg:py-32">
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
            {/* Photo */}
            <div className="reveal md:w-[40%] flex justify-center md:justify-start">
              <img
                src="/author-photo-320.webp"
                alt="Fareal Ahmed"
                loading="lazy"
                decoding="async"
                className="w-full max-w-[320px] rounded-xl border border-border-subtle object-cover"
              />
            </div>

            {/* Text */}
            <div className="md:w-[60%]">
              <p className="reveal text-section-label mb-4">
                ABOUT THE INSTRUCTOR
              </p>
              <h2 className="reveal font-heading text-h2 text-text-primary mb-4">
                Fareal Ahmed
              </h2>
              <p className="reveal font-body text-body text-text-secondary mb-6">
                Software engineer with 4+ years of experience building for the
                education sector — passionate about edtech, schools, and the
                everyday work of students and teachers. I build practical,
                approachable courses that turn modern web development into
                something teachers can teach and learners can ship, focused on
                the tools and habits that hold up in real classrooms and real
                products.
              </p>

              <div className="reveal flex items-center gap-4">
                <a
                  href={GITHUB}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-accent-cyan hover:scale-[1.15] transition-all duration-150"
                  aria-label="GitHub"
                >
                  <Github size={24} />
                </a>
                <a
                  href={LINKEDIN}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-accent-cyan hover:scale-[1.15] transition-all duration-150"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 5 — PRICING / CTA                                    */}
      {/* ============================================================ */}
      <section id="pricing" className="section-pricing bg-bg-primary py-24 lg:py-32 relative overflow-hidden">
        {/* Subtle glow */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(34,211,238,0.04) 0%, transparent 70%)',
          }}
        />

        <div className="max-w-content mx-auto px-6 lg:px-10 relative">
          <div className="text-center mb-12">
            <h2 className="reveal font-heading text-h2 text-text-primary mb-4">
              Process beats prompts.
            </h2>
            <p className="reveal font-body text-body text-text-secondary max-w-[480px] mx-auto">
              Free access to every lesson — sign in with your email and start watching.
            </p>
          </div>

          {/* Access Card */}
          <div className="reveal max-w-[400px] mx-auto">
            <div className="bg-bg-elevated border border-border-subtle rounded-xl p-10 hover:border-border-active hover:-translate-y-1 hover:shadow-glow-lg transition-all duration-300">
              <div className="flex items-baseline justify-center gap-2 mb-6">
                <span className="font-heading text-hero text-accent-cyan">Free</span>
              </div>

              <div className="border-t border-border-subtle pt-6 mb-6">
                <ul className="space-y-3">
                  {[
                    `${totalLessons} video lesson${totalLessons !== 1 ? 's' : ''} (and growing)`,
                    `${totalH > 0 ? `${totalH}h ` : ''}${totalM}m of content so far`,
                    'Lifetime access',
                    'All future updates',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check size={16} className="text-success shrink-0" />
                      <span className="font-body text-sm text-text-secondary">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {isLoggedIn ? (
                <Link
                  to="/dashboard"
                  className="block w-full text-center font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-6 py-3 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline"
                >
                  Go to Dashboard
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="block w-full text-center font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-6 py-3 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline"
                >
                  Get access
                </Link>
              )}

            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
